/**
 * ALDER SEARCH — Navigation Component
 * Design: "Alder Forest — Organic Precision"
 * Forest green background on scroll, transparent on hero
 * Brass accent on hover, Playfair Display for logo
 */

import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "wouter";

const navLinks = [
  { label: "Approach", href: "/approach" },
  { label: "Sectors", href: "#sectors" },
  { label: "Leadership", href: "#team" },
  { label: "Insights", href: "#insights" },
];

const alderSearchLinks = [
  { label: "CPO Search", href: "/executive-search/cpo-search" },
  { label: "Product Board Director Placement", href: "/executive-search/board-director-placement" },
  { label: "Product Leadership Team Buildouts", href: "/executive-search/leadership-team-buildouts" },
  { label: "Fractional & Interim Product Leadership", href: "/executive-search/fractional-interim-leadership" },
];

const alderTalentLinks = [
  { label: "Head of Product Search", href: "/senior-leadership/head-of-product-search" },
  { label: "VP Product & Senior IC Search", href: "/senior-leadership/vp-specialist-search" },
  { label: "Senior IC & Staff Product Search", href: "/senior-leadership" },
];

const linkStyle = {
  fontFamily: "'DM Sans', system-ui, sans-serif",
  fontWeight: 400,
  fontSize: "0.8rem",
  letterSpacing: "0.01em",
  color: "oklch(0.82 0.015 80)",
  textDecoration: "none",
  display: "block",
  padding: "5px 0",
  transition: "color 0.15s ease",
};

export default function Navigation() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [practicesOpen, setPracticesOpen] = useState(false);
  const [location] = useLocation();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setPracticesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleNavClick = (href: string) => {
    setMenuOpen(false);
    setPracticesOpen(false);
    if (href.startsWith("#")) {
      if (location !== "/") {
        window.location.href = "/" + href;
        return;
      }
      const el = document.querySelector(href);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  const navButtonStyle = (isScrolled: boolean) => ({
    fontFamily: "'DM Sans', system-ui, sans-serif",
    fontWeight: 500,
    color: isScrolled
      ? "oklch(0.85 0.015 80)"
      : "rgba(255,255,255,0.85)",
    background: "none",
    border: "none",
    padding: "4px 0",
    letterSpacing: "0.04em",
  });

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#1B3A2D] shadow-lg shadow-black/20"
          : "bg-transparent"
      }`}
    >
      <div className="container">
        <nav className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group">
            <div className="flex items-center gap-2">
              <svg
                width="32"
                height="36"
                viewBox="0 0 32 36"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                className="transition-transform duration-300 group-hover:scale-105"
              >
                <path
                  d="M16 2C16 2 4 8 4 18C4 24.627 9.373 30 16 30C22.627 30 28 24.627 28 18C28 8 16 2 16 2Z"
                  fill="oklch(0.62 0.085 75)"
                  opacity="0.9"
                />
                <path
                  d="M16 30L16 35"
                  stroke="oklch(0.62 0.085 75)"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
                <path
                  d="M16 10L16 26"
                  stroke="oklch(0.245 0.065 155)"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  opacity="0.6"
                />
                <path
                  d="M16 16L11 13"
                  stroke="oklch(0.245 0.065 155)"
                  strokeWidth="1"
                  strokeLinecap="round"
                  opacity="0.5"
                />
                <path
                  d="M16 20L21 17"
                  stroke="oklch(0.245 0.065 155)"
                  strokeWidth="1"
                  strokeLinecap="round"
                  opacity="0.5"
                />
              </svg>
              <span
                className="font-display text-xl font-700 tracking-tight"
                style={{
                  color: scrolled ? "oklch(0.952 0.012 80)" : "white",
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontWeight: 700,
                }}
              >
                Alder Search
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-8">
            {/* Practices Dropdown */}
            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setPracticesOpen(!practicesOpen)}
                className="font-ui text-sm font-500 tracking-wide brass-underline transition-colors duration-200 flex items-center gap-1.5"
                style={{
                  ...navButtonStyle(scrolled),
                  fontSize: "0.875rem",
                }}
              >
                Practices
                <svg
                  width="10"
                  height="6"
                  viewBox="0 0 10 6"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  className="transition-transform duration-200"
                  style={{
                    transform: practicesOpen ? "rotate(180deg)" : "rotate(0deg)",
                    opacity: 0.7,
                  }}
                >
                  <path
                    d="M1 1L5 5L9 1"
                    stroke={scrolled ? "oklch(0.85 0.015 80)" : "rgba(255,255,255,0.85)"}
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>

              {/* Two-column mega-menu dropdown */}
              {practicesOpen && (
                <div
                  className="absolute top-full left-0 mt-2 z-50"
                  style={{
                    background: "oklch(0.18 0.055 155)",
                    border: "1px solid oklch(0.32 0.07 155)",
                    borderRadius: "4px",
                    boxShadow: "0 8px 32px rgba(0,0,0,0.35)",
                    minWidth: "520px",
                  }}
                >
                  <div className="grid grid-cols-2 gap-0">
                    {/* Column 1: Alder Search */}
                    <div
                      className="p-5"
                      style={{ borderRight: "1px solid oklch(0.28 0.065 155)" }}
                    >
                      <div
                        className="mb-3"
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.6rem",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "oklch(0.62 0.085 75)",
                        }}
                      >
                        Alder Search — Executive Search
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {alderSearchLinks.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            onClick={() => setPracticesOpen(false)}
                            style={linkStyle}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)";
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.82 0.015 80)";
                            }}
                          >
                            {link.label}
                          </Link>
                        ))}
                        <Link
                          href="/executive-search"
                          onClick={() => setPracticesOpen(false)}
                          style={{
                            ...linkStyle,
                            marginTop: "8px",
                            color: "oklch(0.62 0.085 75)",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.78 0.1 75)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
                          }}
                        >
                          View all →
                        </Link>
                      </div>
                    </div>

                    {/* Column 2: Alder Talent */}
                    <div className="p-5">
                      <div
                        className="mb-3"
                        style={{
                          fontFamily: "'DM Mono', monospace",
                          fontSize: "0.6rem",
                          letterSpacing: "0.12em",
                          textTransform: "uppercase",
                          color: "oklch(0.62 0.085 75)",
                        }}
                      >
                        Alder Talent — Senior Leadership
                      </div>
                      <div className="flex flex-col gap-0.5">
                        {alderTalentLinks.map((link) => (
                          <Link
                            key={link.href}
                            href={link.href}
                            onClick={() => setPracticesOpen(false)}
                            style={linkStyle}
                            onMouseEnter={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.72 0.09 75)";
                            }}
                            onMouseLeave={(e) => {
                              (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.82 0.015 80)";
                            }}
                          >
                            {link.label}
                          </Link>
                        ))}
                        <Link
                          href="/senior-leadership"
                          onClick={() => setPracticesOpen(false)}
                          style={{
                            ...linkStyle,
                            marginTop: "8px",
                            color: "oklch(0.62 0.085 75)",
                            fontSize: "0.75rem",
                            fontWeight: 500,
                          }}
                          onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.78 0.1 75)";
                          }}
                          onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.color = "oklch(0.62 0.085 75)";
                          }}
                        >
                          View all →
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) =>
              link.href.startsWith("/") ? (
                <Link
                  key={link.label}
                  href={link.href}
                  className="font-ui text-sm font-500 tracking-wide brass-underline transition-colors duration-200"
                  style={{
                    ...navButtonStyle(scrolled),
                    fontSize: "0.875rem",
                    textDecoration: "none",
                  }}
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => handleNavClick(link.href)}
                  className="font-ui text-sm font-500 tracking-wide brass-underline transition-colors duration-200"
                  style={{
                    ...navButtonStyle(scrolled),
                    fontSize: "0.875rem",
                  }}
                >
                  {link.label}
                </button>
              )
            )}
          </div>

          {/* CTA Button */}
          <div className="hidden md:flex items-center gap-4">
            <button
              onClick={() => handleNavClick("#contact")}
              className="font-ui text-sm font-600 px-6 py-2.5 transition-all duration-300"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
                fontSize: "0.75rem",
                border: "1px solid oklch(0.62 0.085 75)",
                color: scrolled ? "oklch(0.62 0.085 75)" : "white",
                background: "transparent",
                borderRadius: "2px",
              }}
              onMouseEnter={(e) => {
                (e.target as HTMLButtonElement).style.background = "oklch(0.62 0.085 75)";
                (e.target as HTMLButtonElement).style.color = "oklch(0.245 0.065 155)";
              }}
              onMouseLeave={(e) => {
                (e.target as HTMLButtonElement).style.background = "transparent";
                (e.target as HTMLButtonElement).style.color = scrolled
                  ? "oklch(0.62 0.085 75)"
                  : "white";
              }}
            >
              Book a call
            </button>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden flex flex-col gap-1.5 p-2"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label="Toggle menu"
          >
            <span
              className={`block w-6 h-px transition-all duration-300 ${menuOpen ? "rotate-45 translate-y-2" : ""}`}
              style={{ background: scrolled ? "oklch(0.952 0.012 80)" : "white" }}
            />
            <span
              className={`block w-6 h-px transition-all duration-300 ${menuOpen ? "opacity-0" : ""}`}
              style={{ background: scrolled ? "oklch(0.952 0.012 80)" : "white" }}
            />
            <span
              className={`block w-6 h-px transition-all duration-300 ${menuOpen ? "-rotate-45 -translate-y-2" : ""}`}
              style={{ background: scrolled ? "oklch(0.952 0.012 80)" : "white" }}
            />
          </button>
        </nav>
      </div>

      {/* Mobile Menu */}
      <div
        className={`md:hidden transition-all duration-400 overflow-hidden ${
          menuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        }`}
        style={{ background: "oklch(0.18 0.055 155)" }}
      >
        <div className="container py-6 flex flex-col gap-1">
          {/* Alder Search group */}
          <div className="mb-3">
            <div
              className="py-1 mb-1"
              style={{
                fontFamily: "'DM Mono', monospace",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontSize: "0.6rem",
              }}
            >
              Alder Search — Executive Search
            </div>
            {alderSearchLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block font-ui text-sm font-400 py-2"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.82 0.015 80)",
                  textDecoration: "none",
                  borderBottom: "1px solid oklch(0.26 0.065 155)",
                  paddingLeft: "12px",
                }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/executive-search"
              onClick={() => setMenuOpen(false)}
              className="block font-ui text-sm font-500 py-2"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.62 0.085 75)",
                textDecoration: "none",
                borderBottom: "1px solid oklch(0.26 0.065 155)",
                paddingLeft: "12px",
              }}
            >
              View all →
            </Link>
          </div>

          {/* Alder Talent group */}
          <div className="mb-3">
            <div
              className="py-1 mb-1"
              style={{
                fontFamily: "'DM Mono', monospace",
                color: "oklch(0.62 0.085 75)",
                letterSpacing: "0.12em",
                textTransform: "uppercase",
                fontSize: "0.6rem",
              }}
            >
              Alder Talent — Senior Leadership
            </div>
            {alderTalentLinks.map((link) => (
              <Link
                key={link.href}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block font-ui text-sm font-400 py-2"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.82 0.015 80)",
                  textDecoration: "none",
                  borderBottom: "1px solid oklch(0.26 0.065 155)",
                  paddingLeft: "12px",
                }}
              >
                {link.label}
              </Link>
            ))}
            <Link
              href="/senior-leadership"
              onClick={() => setMenuOpen(false)}
              className="block font-ui text-sm font-500 py-2"
              style={{
                fontFamily: "'DM Sans', system-ui, sans-serif",
                color: "oklch(0.62 0.085 75)",
                textDecoration: "none",
                borderBottom: "1px solid oklch(0.26 0.065 155)",
                paddingLeft: "12px",
              }}
            >
              View all →
            </Link>
          </div>

          {/* Other nav links */}
          {navLinks.map((link) =>
            link.href.startsWith("/") ? (
              <Link
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="block text-left font-ui text-base font-400 py-2"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.85 0.015 80)",
                  textDecoration: "none",
                  borderBottom: "1px solid oklch(0.32 0.07 155)",
                }}
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                onClick={() => handleNavClick(link.href)}
                className="text-left font-ui text-base font-400 py-2 transition-colors duration-200"
                style={{
                  fontFamily: "'DM Sans', system-ui, sans-serif",
                  color: "oklch(0.85 0.015 80)",
                  background: "none",
                  border: "none",
                  borderBottom: "1px solid oklch(0.32 0.07 155)",
                }}
              >
                {link.label}
              </button>
            )
          )}

          <button
            onClick={() => handleNavClick("#contact")}
            className="mt-4 font-ui text-sm font-600 px-6 py-3 text-center"
            style={{
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontWeight: 600,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
              fontSize: "0.75rem",
              background: "oklch(0.62 0.085 75)",
              color: "oklch(0.245 0.065 155)",
              border: "none",
              borderRadius: "2px",
            }}
          >
            Engage Us
          </button>
        </div>
      </div>
    </header>
  );
}
